import React, { useEffect } from "react";
import {
    Avatar,
    Box,
    Chip,
    Link,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import CodeOutlinedIcon from "@mui/icons-material/CodeOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import TitleDescription from "../components/TitleDescription";
import Title from "../components/Title";
import TitleDescriptionImage from "../components/TitleDescriptionImage";
import { ReactComponent as ComprehensiveAnalyticsReportingComponent } from "../assets/comprehensive-analytics-reporting.svg";
import { ReactComponent as SeamlessIntegrationWithCBSComponent } from "../assets/seamless-integration-with-cbs.svg";
import { ReactComponent as SeamlessSupport247Component } from "../assets/247-support.svg";
import { useLocation } from "react-router-dom";

function navigateToSection(hash: string) {
    const element = document.getElementById(hash);
    if (element) {
        var headerOffset = 45;
        var elementPosition = element.getBoundingClientRect().top;
        var offsetPosition = elementPosition + window.scrollY - headerOffset;
        window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
        });
    }
}

const PRIMARY_COLOR = "#6C4FE0";
const BORDER_COLOR = "#E2E8F0";
const FLOW_MAX_WIDTH = 900;

const inlineCodeStyle = {
    backgroundColor: "#F1F3F4",
    color: "#1A202C",
    borderRadius: "4px",
    padding: "1px 6px",
    fontFamily: '"SF Mono","Monaco","Inconsolata","Roboto Mono",monospace',
    fontSize: "12.5px",
};

const codeBlockStyle = {
    backgroundColor: "#0F172A",
    color: "#E2E8F0",
    borderRadius: "6px",
    padding: "14px 18px",
    fontFamily: '"SF Mono","Monaco","Inconsolata","Roboto Mono",monospace',
    fontSize: "12.5px",
    lineHeight: 1.6,
    whiteSpace: "pre" as const,
    overflowX: "auto" as const,
    marginTop: "8px",
    marginBottom: "8px",
};

const sampleAccountXml = `<Account type="deposit" maskedAccNumber="XXXXX0966">
  <Profile>
    <Holders type="SINGLE">
      <Holder
        name="fName mName lName"
        mobile="9309107496"
        pan="JDAJB0749G" />
    </Holders>
  </Profile>
  <Summary
    currentBalance="41100.00"
    currency="INR"
    type="SAVINGS"
    ifscCode="SBP0000136" />
  <Transactions startDate="2025-05-19">
    <Transaction .../>
    <Transaction .../>
  </Transactions>
</Account>`;

interface FipEntry {
    name: string;
    fipId: string;
    fiType: string;
    category: "Bank" | "Insurance" | "Demat / RTA" | "Mutual fund" | "GST";
}

const CATEGORY_STYLES: Record<
    FipEntry["category"],
    { bg: string; color: string; border: string }
> = {
    Bank: { bg: "#E8F0FE", color: "#1967D2", border: "#D2E3FC" },
    Insurance: { bg: "#FCE4EC", color: "#C2185B", border: "#F8BBD0" },
    "Demat / RTA": { bg: "#E6F4EA", color: "#137333", border: "#CEEAD6" },
    "Mutual fund": { bg: "#F3E8FF", color: "#6B21A8", border: "#E9D5FF" },
    GST: { bg: "#FEF3C7", color: "#92400E", border: "#FDE68A" },
};

const FIP_CATALOGUE: FipEntry[] = [
    { name: "Finvu Bank", fipId: "BARB0KIMXXX", fiType: "DEPOSIT", category: "Bank" },
    { name: "Dhanagar Bank", fipId: "dhanagarbank", fiType: "DEPOSIT", category: "Bank" },
    { name: "Finsure", fipId: "fip@finsure", fiType: "LIFE_INSURANCE", category: "Insurance" },
    { name: "Finsure General", fipId: "fip@finsuregeneral", fiType: "GENERAL_INSURANCE", category: "Insurance" },
    { name: "Finrepo", fipId: "fip@finrepo", fiType: "MUTUAL_FUNDS / EQUITIES", category: "Demat / RTA" },
    { name: "FIP insurance policies", fipId: "fip@inspol", fiType: "INSURANCE", category: "Insurance" },
    { name: "General Insurance FIP", fipId: "fip@general", fiType: "GENERAL_INSURANCE", category: "Insurance" },
    { name: "Life Insurance FIP", fipId: "fip@life", fiType: "LIFE_INSURANCE", category: "Insurance" },
    { name: "Finvu GSTN", fipId: "fip@finvugst", fiType: "GSTR1_3B", category: "GST" },
    { name: "MFC", fipId: "fip@finmfc", fiType: "MUTUAL_FUNDS", category: "Mutual fund" },
];

interface StepCardProps {
    stepNumber: number;
    icon: React.ReactNode;
    title: string;
    isLast?: boolean;
    children: React.ReactNode;
}

const StepCard: React.FC<StepCardProps> = ({ stepNumber, icon, title, isLast, children }) => (
    <Box sx={{ display: "flex", position: "relative" }}>
        <Box
            sx={{
                flexShrink: 0,
                mr: 2.5,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
            }}
        >
            <Avatar
                sx={{
                    bgcolor: PRIMARY_COLOR,
                    width: 36,
                    height: 36,
                    fontSize: 15,
                    fontWeight: "bold",
                    zIndex: 1,
                    boxShadow: "0 2px 4px rgba(108, 79, 224, 0.25)",
                }}
            >
                {stepNumber}
            </Avatar>
            {!isLast && (
                <Box sx={{ flex: 1, width: 2, backgroundColor: BORDER_COLOR, mt: 0.5 }} />
            )}
        </Box>
        <Paper
            elevation={0}
            sx={{
                p: 2.5,
                mb: isLast ? 0 : 3,
                border: `1px solid ${BORDER_COLOR}`,
                borderLeft: `4px solid ${PRIMARY_COLOR}`,
                borderRadius: 2,
                flex: 1,
                minWidth: 0,
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                <Box sx={{ color: PRIMARY_COLOR, display: "inline-flex", mr: 1 }}>
                    {icon}
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#1A202C" }}>
                    {title}
                </Typography>
            </Box>
            {children}
        </Paper>
    </Box>
);

const SEQUENCE_ACTORS = [
    { id: "consumer", label: "Consumer", sub: "Mobile + PAN" },
    { id: "fiu", label: "FIU", sub: "Your app" },
    { id: "aa", label: "AA", sub: "Finvu · Setu · OneMoney" },
    { id: "simbanks", label: "SimBanks (FIP)", sub: "Dummy data" },
] as const;

type SequenceActorId = typeof SEQUENCE_ACTORS[number]["id"];

interface SequenceMessage {
    from: SequenceActorId;
    to: SequenceActorId;
    label: string;
    note?: string;
}

const SEQUENCE_MESSAGES: SequenceMessage[] = [
    { from: "consumer", to: "fiu", label: "1. Initiate journey" },
    { from: "fiu", to: "aa", label: "2. Raise consent request", note: "mobile + PAN" },
    { from: "aa", to: "simbanks", label: "3. Discover accounts" },
    { from: "simbanks", to: "aa", label: "4. Return matching accounts" },
    { from: "aa", to: "consumer", label: "5. Prompt for consent + OTP" },
    { from: "consumer", to: "aa", label: "6. Approve & link" },
    { from: "aa", to: "simbanks", label: "7. Fetch FI data" },
    { from: "simbanks", to: "aa", label: "8. Serve ReBIT XML" },
    { from: "aa", to: "fiu", label: "9. Deliver encrypted FI data" },
];

const SequenceDiagram: React.FC = () => {
    const actorCount = SEQUENCE_ACTORS.length;
    const headerHeight = 44;
    const rowHeight = 58;
    const sideMargin = 24;
    const totalHeight = headerHeight + SEQUENCE_MESSAGES.length * rowHeight + 24;
    const actorIndex: Record<SequenceActorId, number> = {
        consumer: 0,
        fiu: 1,
        aa: 2,
        simbanks: 3,
    };

    return (
        <Box
            sx={{
                position: "relative",
                border: `1px solid ${BORDER_COLOR}`,
                borderRadius: 2,
                p: 2,
                overflowX: "auto",
                backgroundColor: "#FFFFFF",
                maxWidth: FLOW_MAX_WIDTH,
            }}
        >
            <Box sx={{ position: "relative", minWidth: 640, height: totalHeight }}>
                {/* Actor headers */}
                <Box sx={{ display: "flex", height: headerHeight }}>
                    {SEQUENCE_ACTORS.map((actor) => (
                        <Box
                            key={actor.id}
                            sx={{
                                flex: 1,
                                textAlign: "center",
                                px: 1,
                            }}
                        >
                            <Box
                                sx={{
                                    display: "inline-block",
                                    px: 1.5,
                                    py: 0.5,
                                    borderRadius: 1,
                                    backgroundColor: "#F5F3FF",
                                    border: `1px solid #E9E4FB`,
                                    color: PRIMARY_COLOR,
                                    fontWeight: 700,
                                    fontSize: 13,
                                }}
                            >
                                {actor.label}
                            </Box>
                            <Typography fontSize={11} sx={{ color: "#5B6B85", mt: 0.5 }}>
                                {actor.sub}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                {/* Lifelines */}
                {SEQUENCE_ACTORS.map((_, i) => (
                    <Box
                        key={i}
                        sx={{
                            position: "absolute",
                            top: headerHeight,
                            bottom: 0,
                            left: `calc(${sideMargin}px + (100% - ${sideMargin * 2}px) * ${i} / ${actorCount - 1})`,
                            width: 0,
                            borderLeft: `1px dashed #CBD5E1`,
                        }}
                    />
                ))}

                {/* Messages */}
                {SEQUENCE_MESSAGES.map((msg, idx) => {
                    const fromIdx = actorIndex[msg.from];
                    const toIdx = actorIndex[msg.to];
                    const isRight = toIdx > fromIdx;
                    const leftIdx = Math.min(fromIdx, toIdx);
                    const rightIdx = Math.max(fromIdx, toIdx);
                    const top = headerHeight + idx * rowHeight + rowHeight / 2;
                    return (
                        <Box
                            key={idx}
                            sx={{
                                position: "absolute",
                                top: `${top}px`,
                                left: `calc(${sideMargin}px + (100% - ${sideMargin * 2}px) * ${leftIdx} / ${actorCount - 1})`,
                                width: `calc((100% - ${sideMargin * 2}px) * ${rightIdx - leftIdx} / ${actorCount - 1})`,
                                transform: "translateY(-50%)",
                                pointerEvents: "none",
                            }}
                        >
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: "50%",
                                    left: 0,
                                    right: 0,
                                    height: 2,
                                    backgroundColor: PRIMARY_COLOR,
                                    transform: "translateY(-50%)",
                                    opacity: 0.8,
                                }}
                            />
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: "50%",
                                    [isRight ? "right" : "left"]: 0,
                                    width: 0,
                                    height: 0,
                                    transform: "translateY(-50%)",
                                    borderTop: "5px solid transparent",
                                    borderBottom: "5px solid transparent",
                                    [isRight ? "borderLeft" : "borderRight"]: `8px solid ${PRIMARY_COLOR}`,
                                }}
                            />
                            <Box
                                sx={{
                                    position: "absolute",
                                    bottom: "calc(50% + 4px)",
                                    left: 0,
                                    right: 0,
                                    textAlign: "center",
                                }}
                            >
                                <Typography
                                    component="span"
                                    fontSize={12}
                                    fontWeight={600}
                                    sx={{
                                        color: "#1A202C",
                                        backgroundColor: "#FFFFFF",
                                        px: 1,
                                    }}
                                >
                                    {msg.label}
                                </Typography>
                            </Box>
                            {msg.note && (
                                <Box
                                    sx={{
                                        position: "absolute",
                                        top: "calc(50% + 4px)",
                                        left: 0,
                                        right: 0,
                                        textAlign: "center",
                                    }}
                                >
                                    <Typography
                                        component="span"
                                        fontSize={11}
                                        sx={{
                                            color: "#5B6B85",
                                            backgroundColor: "#FFFFFF",
                                            px: 1,
                                            fontFamily: '"SF Mono","Monaco","Inconsolata","Roboto Mono",monospace',
                                        }}
                                    >
                                        {msg.note}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};

const SimBanksPage: React.FC = React.memo(() => {
    const location = useLocation();
    useEffect(() => {
        if (location.hash.length === 0) {
            window.scrollTo({
                top: 0,
                behavior: "smooth",
            });
            return;
        }

        const hash = location.hash.slice(1);
        navigateToSection(hash);
    }, [location.hash]);

    return (
        <>
            <Box sx={{ paddingLeft: 4, paddingRight: 4 }}>
                <TitleDescription
                    title="SimBanks"
                    description="SimBanks is a Finfactor-operated FIP simulator for the India Account Aggregator (AA) ecosystem. Spin up dummy bank, demat, mutual fund, GST and insurance accounts against a mobile number, then exercise the full AA journey end-to-end against real AAs like Finvu, Setu and OneMoney."
                />
                <Box sx={{ mt: 1 }}>
                    <Link
                        href="https://simbanks.finfactor.in/"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            display: "inline-block",
                            fontFamily: '"SF Mono","Monaco","Inconsolata","Roboto Mono",monospace',
                            fontSize: 13,
                            color: PRIMARY_COLOR,
                            backgroundColor: "#F5F3FF",
                            border: `1px solid #E9E4FB`,
                            borderRadius: 999,
                            padding: "4px 12px",
                            textDecoration: "none",
                        }}
                    >
                        simbanks.finfactor.in
                    </Link>
                </Box>

                <Box id="why" sx={{ scrollMarginTop: "60px" }}>
                    <Title title="Why SimBanks?" />
                    <Grid container spacing={3} paddingTop={1}>
                        <Grid size={4}>
                            <TitleDescriptionImage
                                title="Full FIP coverage"
                                description="Dummy bank, demat, mutual fund, GST and life/general insurance FIPs out of the box — exercise every consent template you care about without waiting on production data."
                                svg={ComprehensiveAnalyticsReportingComponent}
                                maxWidth={290}
                            />
                        </Grid>
                        <Grid size={4}>
                            <TitleDescriptionImage
                                title="Tune the data"
                                description="Edit the underlying ReBIT XML on any account to test edge cases — zero balances, negative balances, distorted data, holder mismatches — in seconds."
                                svg={SeamlessIntegrationWithCBSComponent}
                                maxWidth={290}
                            />
                        </Grid>
                        <Grid size={4}>
                            <TitleDescriptionImage
                                title="End-to-end UAT"
                                description="Drive the full AA chain: SimBanks → AA (Finvu / Setu / OneMoney) → your FIU. Discover, link, fetch — all against accounts you control."
                                svg={SeamlessSupport247Component}
                                maxWidth={290}
                            />
                        </Grid>
                    </Grid>
                </Box>

                <Title title="Where SimBanks fits in the AA chain" paddingTop={6} paddingBottom={2} />
                <Typography fontSize={14} gutterBottom sx={{ maxWidth: FLOW_MAX_WIDTH, mb: 2 }}>
                    During UAT, SimBanks plays the FIP role. Your FIU drives the journey; the AA brokers consent and routes data fetches to SimBanks instead of a real bank.
                </Typography>
                <SequenceDiagram />
                <Typography fontSize={12.5} sx={{ color: "#5B6B85", mt: 1.5, maxWidth: FLOW_MAX_WIDTH }}>
                    SimBanks is the only piece you control directly — every other actor behaves exactly as it would in production.
                </Typography>

                <Box id="flow" sx={{ scrollMarginTop: "60px" }} />
                <Title title="End-to-end flow" paddingTop={6} paddingBottom={1} />
                <Typography fontSize={14} gutterBottom sx={{ maxWidth: FLOW_MAX_WIDTH }}>
                    A typical UAT run with SimBanks is three steps: seed an account, kick off the AA journey from your FIU, then verify the linked handle on the manage page.
                </Typography>

                <Box sx={{ mt: 3, maxWidth: FLOW_MAX_WIDTH }}>
                    <StepCard
                        stepNumber={1}
                        icon={<AddCircleOutlineOutlinedIcon fontSize="small" />}
                        title="Create a dummy account"
                    >
                        <Typography fontSize={14} sx={{ mb: 1.5 }}>
                            On the landing page at{" "}
                            <Link href="https://simbanks.finfactor.in/" target="_blank" rel="noopener noreferrer">
                                simbanks.finfactor.in
                            </Link>
                            , fill in the <strong>Create a Testing Bank Account</strong> form:
                        </Typography>
                        <Stack spacing={0.75} sx={{ mb: 1.5 }}>
                            <Typography fontSize={13.5}>
                                • <strong>Mobile Number</strong> — the identifier the AA will look up during discovery (e.g.{" "}
                                <Box component="code" sx={inlineCodeStyle}>9309107496</Box>).
                            </Typography>
                            <Typography fontSize={13.5}>
                                • <strong>Dummy Bank</strong> — pick the FIP to simulate (Finvu Bank, Dhanagar Bank, Finsure, Finrepo, GSTN, ...).
                            </Typography>
                            <Typography fontSize={13.5}>
                                • <strong>FI type</strong> & <strong>Account type</strong> — e.g.{" "}
                                <Box component="code" sx={inlineCodeStyle}>DEPOSIT</Box> /{" "}
                                <Box component="code" sx={inlineCodeStyle}>SAVINGS</Box>. Match this to the consent template your FIU will raise.
                            </Typography>
                            <Typography fontSize={13.5}>
                                • <strong>Schema</strong> — leave on <Box component="code" sx={inlineCodeStyle}>v1</Box> unless you have a specific reason.
                            </Typography>
                            <Typography fontSize={13.5}>
                                • <strong>From</strong> / <strong>To</strong> — the date window for generated transactions. Keep this a realistic 12-month window.
                            </Typography>
                        </Stack>
                        <Typography fontSize={14}>
                            Hit <strong>Create Account</strong>. SimBanks generates a dummy account with a fresh account number and a ReBIT-compliant data set.
                        </Typography>
                    </StepCard>

                    <StepCard
                        stepNumber={2}
                        icon={<ManageAccountsOutlinedIcon fontSize="small" />}
                        title="Find the PAN on Manage Accounts"
                    >
                        <Typography fontSize={14} sx={{ mb: 1 }}>
                            Open{" "}
                            <Link
                                href="https://simbanks.finfactor.in/manage-accounts"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                simbanks.finfactor.in/manage-accounts
                            </Link>
                            , search for the mobile number you used, and note the <strong>Registered PAN</strong> shown at the top of the page (e.g.{" "}
                            <Box component="code" sx={inlineCodeStyle}>JDAJB0749G</Box>).
                        </Typography>
                        <Typography fontSize={14} sx={{ mb: 1 }}>
                            Use this mobile + PAN combination when initiating discovery from your FIU — the AA needs them to route the request back to SimBanks.
                        </Typography>
                        <Typography fontSize={14}>
                            The table below the PAN lists every dummy account tied to that mobile, along with the FIP, account number, FI type and the AA handle once linked.
                        </Typography>
                    </StepCard>

                    <StepCard
                        stepNumber={3}
                        icon={<CheckCircleOutlineOutlinedIcon fontSize="small" />}
                        title="Run the AA journey from your FIU"
                    >
                        <Stack spacing={1}>
                            <Typography fontSize={14}>
                                <strong>Request</strong> — your FIU raises a consent on the AA (Finvu / Setu / OneMoney) using the mobile + PAN.
                            </Typography>
                            <Typography fontSize={14}>
                                <strong>Discover</strong> — the AA queries SimBanks and finds the dummy accounts matching the identifiers.
                            </Typography>
                            <Typography fontSize={14}>
                                <strong>Link (OTP)</strong> — approve the consent on the AA UI. SimBanks accepts any OTP for dummy accounts.
                            </Typography>
                            <Typography fontSize={14}>
                                <strong>Fetch</strong> — once consent is active, the FIU pulls FI data. SimBanks serves the ReBIT XML you configured.
                            </Typography>
                        </Stack>
                        <Box
                            sx={{
                                mt: 1.5,
                                p: 1.25,
                                backgroundColor: "#F5F3FF",
                                border: `1px solid #E9E4FB`,
                                borderRadius: 1.5,
                                fontSize: 13,
                                color: "#4A3FA8",
                            }}
                        >
                            <strong>Verify:</strong> after linking, refresh Manage Accounts — the <Box component="code" sx={inlineCodeStyle}>AA Id</Box> column for that account now shows the AA handle (e.g.{" "}
                            <Box component="code" sx={inlineCodeStyle}>9309107496@finvu</Box>).
                        </Box>
                    </StepCard>

                    <StepCard
                        stepNumber={4}
                        icon={<CodeOutlinedIcon fontSize="small" />}
                        title="Tune the data — edit XML"
                        isLast
                    >
                        <Typography fontSize={14} sx={{ mb: 1 }}>
                            On any row in Manage Accounts, click <strong>Edit XML</strong> to open the account's ReBIT payload. Tweak the fields you care about — balance, holder name, PAN, transactions — and hit <strong>Save</strong>.
                        </Typography>
                        <Box component="pre" sx={codeBlockStyle}>
                            {sampleAccountXml}
                        </Box>
                        <Typography fontSize={13} sx={{ color: "#5B6B85", mt: 1 }}>
                            The XML follows the{" "}
                            <Link href="https://api.rebit.org.in/schema" target="_blank" rel="noopener noreferrer">
                                ReBIT FI schema
                            </Link>
                            . The most-tweaked fields in practice are <Box component="code" sx={inlineCodeStyle}>currentBalance</Box>, the holder block, and the transaction set.
                        </Typography>
                        <Typography fontSize={13} sx={{ color: "#5B6B85", mt: 1 }}>
                            The <em>Allow Distorted Data</em> toggle lets you intentionally emit schema-violating data to test FIU error handling — keep it off for happy-path runs.
                        </Typography>
                    </StepCard>
                </Box>

                <Box id="catalogue" sx={{ scrollMarginTop: "60px" }} />
                <Title title="The dummy FIP catalogue" paddingTop={6} paddingBottom={2} />
                <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{ border: `1px solid ${BORDER_COLOR}`, borderRadius: 2, maxWidth: FLOW_MAX_WIDTH }}
                >
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: "#F8FAFC" }}>
                                <TableCell sx={{ fontWeight: 700, color: "#1A202C", fontSize: 12.5 }}>FIP</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: "#1A202C", fontSize: 12.5 }}>FIP ID</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: "#1A202C", fontSize: 12.5 }}>FI Type</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: "#1A202C", fontSize: 12.5 }}>Category</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {FIP_CATALOGUE.map((entry) => {
                                const style = CATEGORY_STYLES[entry.category];
                                return (
                                    <TableRow key={entry.name} hover>
                                        <TableCell sx={{ fontSize: 13, color: "#1A202C", fontWeight: 600 }}>
                                            {entry.name}
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                fontSize: 12.5,
                                                color: "#324A6D",
                                                fontFamily: '"SF Mono","Monaco","Inconsolata","Roboto Mono",monospace',
                                            }}
                                        >
                                            {entry.fipId}
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                fontSize: 12.5,
                                                color: "#324A6D",
                                                fontFamily: '"SF Mono","Monaco","Inconsolata","Roboto Mono",monospace',
                                            }}
                                        >
                                            {entry.fiType}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={entry.category}
                                                sx={{
                                                    backgroundColor: style.bg,
                                                    color: style.color,
                                                    border: `1px solid ${style.border}`,
                                                    fontWeight: 600,
                                                    fontSize: 11.5,
                                                    height: 22,
                                                }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Box id="do-dont" sx={{ scrollMarginTop: "60px" }} />
                <Title title="Do & don't" paddingTop={6} paddingBottom={2} />
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                border: "1px solid #CEEAD6",
                                backgroundColor: "#E6F4EA",
                                borderRadius: 2,
                                height: "100%",
                            }}
                        >
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                <CheckCircleOutlineOutlinedIcon sx={{ color: "#0F7A3D" }} fontSize="small" />
                                <Typography
                                    fontSize={13}
                                    fontWeight={700}
                                    sx={{ color: "#0F7A3D", textTransform: "uppercase", letterSpacing: 0.6 }}
                                >
                                    Do
                                </Typography>
                            </Stack>
                            <Stack component="ul" sx={{ pl: 2.5, m: 0 }} spacing={0.5}>
                                <Typography component="li" fontSize={13.5} sx={{ color: "#1A202C" }}>
                                    Use a valid-looking 10-digit mobile number.
                                </Typography>
                                <Typography component="li" fontSize={13.5} sx={{ color: "#1A202C" }}>
                                    Match the FI type to the consent template your FIU raises.
                                </Typography>
                                <Typography component="li" fontSize={13.5} sx={{ color: "#1A202C" }}>
                                    Note the <strong>Registered PAN</strong> before initiating AA discovery.
                                </Typography>
                                <Typography component="li" fontSize={13.5} sx={{ color: "#1A202C" }}>
                                    Keep a realistic 12-month transaction window.
                                </Typography>
                            </Stack>
                        </Paper>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                border: "1px solid #F4CAD1",
                                backgroundColor: "#FDE7EB",
                                borderRadius: 2,
                                height: "100%",
                            }}
                        >
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                <CancelOutlinedIcon sx={{ color: "#B00020" }} fontSize="small" />
                                <Typography
                                    fontSize={13}
                                    fontWeight={700}
                                    sx={{ color: "#B00020", textTransform: "uppercase", letterSpacing: 0.6 }}
                                >
                                    Don't
                                </Typography>
                            </Stack>
                            <Stack component="ul" sx={{ pl: 2.5, m: 0 }} spacing={0.5}>
                                <Typography component="li" fontSize={13.5} sx={{ color: "#1A202C" }}>
                                    Put any real customer data into SimBanks.
                                </Typography>
                                <Typography component="li" fontSize={13.5} sx={{ color: "#1A202C" }}>
                                    Toggle <em>Allow Distorted Data</em> on the happy-path run.
                                </Typography>
                                <Typography component="li" fontSize={13.5} sx={{ color: "#1A202C" }}>
                                    Assume persistence across environment resets.
                                </Typography>
                                <Typography component="li" fontSize={13.5} sx={{ color: "#1A202C" }}>
                                    Share a dummy mobile across teams without coordinating.
                                </Typography>
                            </Stack>
                        </Paper>
                    </Grid>
                </Grid>

                <Title title="Quick reference" paddingTop={6} paddingBottom={2} />
                <Grid container spacing={1.5}>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 1.5,
                                border: `1px solid ${BORDER_COLOR}`,
                                borderRadius: 1.5,
                            }}
                        >
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                                <AccountBalanceOutlinedIcon sx={{ color: PRIMARY_COLOR }} fontSize="small" />
                                <Typography fontSize={12} fontWeight={700} sx={{ color: "#5B6B85", textTransform: "uppercase", letterSpacing: 0.6 }}>
                                    Create
                                </Typography>
                            </Stack>
                            <Link
                                href="https://simbanks.finfactor.in/"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ fontSize: 13, fontFamily: '"SF Mono","Monaco","Inconsolata","Roboto Mono",monospace' }}
                            >
                                simbanks.finfactor.in/
                            </Link>
                        </Paper>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 1.5,
                                border: `1px solid ${BORDER_COLOR}`,
                                borderRadius: 1.5,
                            }}
                        >
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                                <ManageAccountsOutlinedIcon sx={{ color: PRIMARY_COLOR }} fontSize="small" />
                                <Typography fontSize={12} fontWeight={700} sx={{ color: "#5B6B85", textTransform: "uppercase", letterSpacing: 0.6 }}>
                                    Manage
                                </Typography>
                            </Stack>
                            <Link
                                href="https://simbanks.finfactor.in/manage-accounts"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ fontSize: 13, fontFamily: '"SF Mono","Monaco","Inconsolata","Roboto Mono",monospace' }}
                            >
                                /manage-accounts
                            </Link>
                        </Paper>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 1.5,
                                border: `1px solid ${BORDER_COLOR}`,
                                borderRadius: 1.5,
                            }}
                        >
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                                <TuneOutlinedIcon sx={{ color: PRIMARY_COLOR }} fontSize="small" />
                                <Typography fontSize={12} fontWeight={700} sx={{ color: "#5B6B85", textTransform: "uppercase", letterSpacing: 0.6 }}>
                                    PAN for discovery
                                </Typography>
                            </Stack>
                            <Typography fontSize={13} sx={{ color: "#1A202C" }}>
                                Top of Manage Accounts
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 1.5,
                                border: `1px solid ${BORDER_COLOR}`,
                                borderRadius: 1.5,
                            }}
                        >
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                                <CheckCircleOutlineOutlinedIcon sx={{ color: PRIMARY_COLOR }} fontSize="small" />
                                <Typography fontSize={12} fontWeight={700} sx={{ color: "#5B6B85", textTransform: "uppercase", letterSpacing: 0.6 }}>
                                    Linked AA
                                </Typography>
                            </Stack>
                            <Typography fontSize={13} sx={{ color: "#1A202C" }}>
                                AA Id column on Manage
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 1.5,
                                border: `1px solid ${BORDER_COLOR}`,
                                borderRadius: 1.5,
                            }}
                        >
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                                <BugReportOutlinedIcon sx={{ color: PRIMARY_COLOR }} fontSize="small" />
                                <Typography fontSize={12} fontWeight={700} sx={{ color: "#5B6B85", textTransform: "uppercase", letterSpacing: 0.6 }}>
                                    Schema spec
                                </Typography>
                            </Stack>
                            <Link
                                href="https://api.rebit.org.in/schema"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ fontSize: 13 }}
                            >
                                api.rebit.org.in
                            </Link>
                        </Paper>
                    </Grid>
                </Grid>

                <Box
                    sx={{
                        mt: 6,
                        mb: 2,
                        py: 2,
                        borderTop: `1px solid ${BORDER_COLOR}`,
                        textAlign: "center",
                    }}
                >
                    <Typography fontSize={12} sx={{ color: "#5B6B85" }}>
                        SimBanks is a Finfactor-operated FIP simulator for the India AA ecosystem.{" "}
                        <Link href="https://simbanks.finfactor.in/" target="_blank" rel="noopener noreferrer">
                            simbanks.finfactor.in
                        </Link>
                    </Typography>
                </Box>

            </Box>
        </>
    );
});

export default SimBanksPage;
